import type { LegalSection } from "./privacyPolicyData";

export interface TermsConditionsData {
  slug: string;
  seo: {
    title: string;
    description: string;
    canonical?: string;
  };
  hero: {
    heading: string;
    lastUpdated: string;
  };
  sections: LegalSection[];
}

export const termsConditionsData: TermsConditionsData = {
  slug: "/terms-and-conditions",
  seo: {
    title: "Terms and Conditions | Seasoft Classes",
    description: "Read the Seasoft Classes Terms and Conditions governing use of our website, online IT training courses, and placement services.",
    canonical: "https://seasoftclasses.com/terms-and-conditions",
  },
  hero: {
    heading: "Terms and Conditions",
    lastUpdated: "January 1, 2025",
  },
  sections: [
    {
      id: "section-1",
      heading: "1. Acceptance of Terms",
      content:
        "By accessing seasoftclasses.com or enrolling in our courses, you agree to these Terms and Conditions. These Terms form a legally binding agreement between you and Seasoft Classes, Naperville, Illinois, USA. If you do not agree, do not use our services.",
    },
    {
      id: "section-2",
      heading: "2. Description of Services",
      items: [
        "Online IT Training: Live instructor-led courses in Power BI, Salesforce, SAS, Java, Cybersecurity, PMP, and 300+ more",
        "Course Materials: Session recordings, study guides, practice datasets, and digital resources",
        "Career Support: Resume review, mock interviews, career counseling, and job placement network",
        "Corporate Training: Customized group IT training programs for organizations",
        "IT Staffing: Connecting qualified IT professionals with employer partners",
      ],
    },
    {
      id: "section-3",
      heading: "3. Enrollment and Registration",
      subsections: [
        {
          heading: "3.1 Eligibility",
          content: "Must be 18 or older, provide accurate information, and have legal capacity to enter agreements.",
        },
        {
          heading: "3.2 Enrollment Confirmation",
          content: "Enrollment is confirmed only upon receipt of full payment or agreed payment arrangement.",
        },
        {
          heading: "3.3 Batch Assignment",
          content: "Students are assigned to batches based on availability and schedule. We reserve the right to reassign when necessary.",
        },
      ],
    },
    {
      id: "section-4",
      heading: "4. Fees, Payment, and Refund Policy",
      subsections: [
        {
          heading: "4.1 Payment Terms",
          content: "Full payment required before course commencement unless a written payment plan is agreed.",
        },
        {
          heading: "4.2 Refund Policy",
          items: [
            "Cancellation 7+ days before start: full refund minus $50 or 5% processing fee",
            "Cancellation within 7 days of start: 50% refund",
            "After first session attended or accessed: no refund",
            "Course cancelled by Seasoft Classes: full refund or free batch transfer",
          ],
        },
        {
          heading: "4.3 Refund Processing",
          content: "Approved refunds processed within 10-15 business days to original payment method.",
        },
      ],
    },
    {
      id: "section-5",
      heading: "5. Course Access and Conduct",
      items: [
        "Course materials and recordings accessible for duration of course plus 30 days after",
        "Recording, distributing, or commercially exploiting session content is strictly prohibited",
        "Professional and respectful conduct required in all sessions",
        "Seasoft Classes may remove students for disruptive or abusive behavior without refund",
        "Regular attendance recommended — missed sessions covered by recordings",
      ],
    },
    {
      id: "section-6",
      heading: "6. Intellectual Property",
      items: [
        "All course content, materials, and recordings are exclusive property of Seasoft Classes",
        "Students receive limited personal-use license for enrolled course materials only",
        "Copying, distributing, selling, or sharing course materials is strictly prohibited",
        "Seasoft Classes trademarks may not be used without prior written permission",
      ],
    },
    {
      id: "section-7",
      heading: "7. Placement and Career Services",
      items: [
        "Placement support is a value-added service — employment is not guaranteed",
        "Students must complete their course and actively engage in placement activities",
        "Employment relationships are solely between the student and the employer",
        "Seasoft Classes bears no liability for employment disputes",
      ],
    },
    {
      id: "section-8",
      heading: "8. Website Use",
      content:
        "Use our website for lawful purposes only. Do not use for fraud, spam, or any illegal activity. We do not guarantee uninterrupted website access and may modify services at any time.",
    },
    {
      id: "section-9",
      heading: "9. Limitation of Liability",
      content:
        "Seasoft Classes is not liable for indirect, incidental, or consequential damages from use of our services. Total liability is limited to fees paid for the specific service in the preceding three months.",
    },
    {
      id: "section-10",
      heading: "10. Disclaimer of Warranties",
      content:
        "Services provided as-is without warranties of any kind. We do not warrant that training will result in employment or salary increases.",
    },
    {
      id: "section-11",
      heading: "11. Indemnification",
      content:
        "You agree to indemnify Seasoft Classes against claims arising from your use of our services or violation of these Terms.",
    },
    {
      id: "section-12",
      heading: "12. Governing Law",
      content:
        "These Terms are governed by Illinois state law. Disputes unresolved in 30 days go to binding arbitration in DuPage County, Illinois. Class action waiver applies.",
    },
    {
      id: "section-13",
      heading: "13. Privacy",
      content:
        "Your use of our services is governed by our Privacy Policy at seasoftclasses.com/privacy-policy, incorporated here by reference.",
    },
    {
      id: "section-14",
      heading: "14. Changes to Terms",
      content:
        "We may modify these Terms at any time. Continued use after changes constitutes acceptance. Material changes will be communicated via email to enrolled students.",
    },
    {
      id: "section-15",
      heading: "15. Contact",
      content:
        "Questions about these Terms: training@seasoftclasses.com | +1 (708) 766-9765 | 15 Huntington Circle, Naperville, IL 60540",
    },
  ],
};

